import React, { useState, useEffect, useMemo } from 'react';
import { CalendarDays, Clock, User, Scissors, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { CustomSelect } from '../../components/CustomSelect';
import { appointments, subscribe, catalogServices } from '../../data/store';
import { motion, AnimatePresence } from 'framer-motion';

export const MyBookingsTab = ({ org, currentUser }) => {
  const [aptList, setAptList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const update = () => {
      // Filter appointments by this org and assigned to the current user
      const filtered = appointments.filter(a => a.orgId === org?.id && a.staffId === currentUser?.id);
      
      // Sort by date then time
      filtered.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time.replace(/ AM| PM/, ':00')}`); // simple parse attempt
        const dateB = new Date(`${b.date}T${b.time.replace(/ AM| PM/, ':00')}`);
        return dateA - dateB;
      });
      
      setAptList(filtered);
    };
    update();
    return subscribe(update);
  }, [org?.id, currentUser?.id]);

  const myServices = useMemo(() => catalogServices.filter(s => s.orgId === org?.id), [org?.id]);

  const filteredAptList = useMemo(() => {
    if (!searchQuery.trim()) return aptList;
    const lowerQuery = searchQuery.toLowerCase();
    
    return aptList.filter(apt => {
      const service = myServices.find(s => s.id === apt.serviceId);
      const serviceName = service ? service.name.toLowerCase() : '';
      const custName = (apt.customerName || '').toLowerCase();
      const phone = (apt.phone || '').toLowerCase();
      
      return custName.includes(lowerQuery) || phone.includes(lowerQuery) || serviceName.includes(lowerQuery);
    });
  }, [aptList, searchQuery, myServices]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  useEffect(() => { setPage(1); }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredAptList.length / perPage));
  const start = (page - 1) * perPage;
  const pageAptList = filteredAptList.slice(start, start + perPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'requested': return 'text-amber-600';
      case 'pending': return 'text-orange-600';
      case 'confirmed': return 'text-sky-600';
      case 'checked-in': return 'text-indigo-600';
      case 'started': return 'text-purple-600';
      case 'completed': return 'text-emerald-600';
      case 'invoiced': return 'text-slate-600';
      case 'cancelled': return 'text-rose-600';
      case 'no-show': return 'text-stone-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer, phone, or service..."
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent transition-shadow"
            />
          </div>
        </div>
      </section>
      
      {filteredAptList.length === 0 ? (
        <div className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-12 text-center text-slate-500 flex flex-col items-center justify-center min-h-[400px]">
          <CalendarDays size={64} className="mb-4 text-slate-300" />
          <h3 className="text-lg font-bold text-slate-700">
            {aptList.length === 0 ? "No upcoming bookings" : "No results found"}
          </h3>
          <p className="text-sm mt-2">
            {aptList.length === 0 
              ? "You don't have any appointments assigned to you right now." 
              : `No bookings match "${searchQuery}".`}
          </p>
        </div>
      ) : (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col w-full">
          <div className="overflow-x-auto custom-scrollbar table-scrollbar">
            <table className="w-full text-left border-collapse relative">
              <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_var(--theme-tertiary)]">
                <tr>
                  <th className="w-[30%] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Customer</th>
                  <th className="w-[25%] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Date & Time</th>
                  <th className="w-[30%] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Service</th>
                  <th className="w-[15%] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--theme-tertiary)] font-poppins">
                <AnimatePresence>
                  {pageAptList.map((apt) => {
                    const service = myServices.find(s => s.id === apt.serviceId);
                    const serviceName = service ? service.name : 'Unknown Service';
                    const aptDate = new Date(apt.date);
                    
                    return (
                      <motion.tr
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        key={apt.id}
                        className="hover:bg-slate-100 transition-colors group"
                      >
                        <td className="px-5 py-3 align-middle">
                          <p className="text-[13px] font-extrabold uppercase tracking-wide text-[#0d0d0d] truncate max-w-[220px]" title={apt.customerName}>{apt.customerName}</p>
                          <p className="text-[11px] font-medium text-slate-600 tracking-wide uppercase mt-0.5">{apt.phone || '-'}</p>
                        </td>
                        <td className="px-5 py-3 align-middle">
                          <p className="text-[13px] font-extrabold uppercase tracking-wide text-[#0d0d0d] truncate">
                            {aptDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-[11px] font-medium text-slate-600 tracking-wide uppercase mt-0.5">{apt.time}</p>
                        </td>
                        <td className="px-5 py-3 align-middle">
                          <p className="text-[13px] font-extrabold uppercase tracking-wide text-[#0d0d0d] truncate max-w-[200px]" title={serviceName}>{serviceName}</p>
                          {apt.notes && (
                            <p className="text-[10px] font-medium text-slate-400 italic mt-0.5 max-w-[200px] truncate uppercase tracking-wider" title={apt.notes}>
                              "{apt.notes}"
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-3 align-middle text-right">
                          <span className={`text-[11px] font-extrabold uppercase tracking-[0.16em] ${getStatusColor(apt.status)}`}>
                            {apt.status}
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <section className="px-5 py-3 flex items-center justify-between text-[11px] font-bold text-slate-500 border-t border-[var(--theme-tertiary)] bg-white shrink-0">
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-widest">Showing</span>
            <CustomSelect
              value={perPage}
              onChange={(val) => {
                setPerPage(Number(val));
                setPage(1);
              }}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
                { value: filteredAptList.length > 0 ? filteredAptList.length : 200, label: 'All' }
              ]}
              className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
              menuUp={true}
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="uppercase tracking-widest">
              PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
            </span>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                <ChevronLeft size={14} />
              </button>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          </section>
        )}
        </section>
        )}
    </div>
  );
};
